import { DEFAULT_PRINT_TYPE_ID, PrintTypeId } from '../api/static/printTypes';
import { DEFAULT_COLOR_ID, GENERIC_COLORS } from '../api/static/colors';
import { DEFAULT_THEME_TYPE, ThemeType } from '../api/static/ThemeType';
import {
  GlassesGetter,
  prefixType as prefixGlassType,
} from '../store/modules/configurator/glasses';
import { DEFAULT_SIZE_ID } from '../api/static/sizes';
import { DEFAULT_SCREEN_PRINTING_COLOR_ID } from '../api/static/screenPrintingColors';
import { AutoIncrementIdFactory } from './AutoIncrement';
import { DEFAULT_ZONE_GROUP_ID } from '../api/static/zoneGroups';
import { ZoneTypeMap } from '../api/static/zones';

export class ConfiguratorError extends Error {}

export class ThemeTypeNotFoundError extends ConfiguratorError {}

export class SizeIdError extends ConfiguratorError {}

export class SizeIdNotFoundError extends SizeIdError {}

export class SizeIdNotAvailableError extends SizeIdError {}

export class ProductIdError extends ConfiguratorError {}

export class ProductIdNotFoundError extends ProductIdError {}

export class ProductIdNotAvailableError extends ProductIdError {}

export class ColorIdError extends ConfiguratorError {}

export class ColorIdNotFoundError extends ColorIdError {}

export class ColorIdNotAvailableError extends ColorIdError {}

export class QuantityIdError extends ConfiguratorError {}

export class QuantityIdNotFoundError extends QuantityIdError {}

export class QuantityIdNotAvailableError extends QuantityIdError {}

export class ScreenPrintingColorIdError extends ConfiguratorError {}

export class ScreenPrintingColorIdNotFoundError extends ScreenPrintingColorIdError {}

/**
 *
 * @param {string} value
 * @param fallback
 * @return {number|null}
 */
export function normalizeId(value, fallback = null) {
  return (value && parseInt(value, 10)) || fallback;
}

/**
 *
 * @param {PrintTypeId} printTypeId
 * @param {ColorId} colorId
 * @param {string} type
 * @return {boolean}
 */
export function isColorAvailable(printTypeId, colorId) {
  return (
    printTypeId === PrintTypeId.SCREEN_PRINTING ||
    GENERIC_COLORS.includes(colorId)
  );
}

/**
 *
 * @param {ConfiguratorState} state
 * @param {ThemeType|string} themeType
 * @param {boolean} fallbackWhenNotFound
 * @return {ThemeType}
 */
export function getThemeType(
  { state },
  themeType = undefined,
  fallbackWhenNotFound = false
) {
  if (themeType) {
    if (Object.values(ThemeType).includes(themeType)) {
      return themeType;
    } else {
      const message = `Theme type not found (${themeType})`;

      if (fallbackWhenNotFound) {
        console.warn(message);
      } else {
        throw new ThemeTypeNotFoundError(message);
      }
    }
  }

  if (state.themeType) {
    console.log(`Keep current theme type ('${state.themeType}')`);

    return state.themeType;
  }

  console.log(`Fallback to the default theme type (${DEFAULT_THEME_TYPE})`);

  return DEFAULT_THEME_TYPE;
}

/**
 *
 * @param {ConfiguratorState} state
 * @param rootGetters
 * @param {number} sizeId
 * @param currentSizeId
 * @param productId
 * @param themeType
 * @param {boolean} fallbackWhenNotFound
 * @return {number}
 * @throws SizeIdNotFoundError
 */
export function getSizeId(
  { rootGetters },
  sizeId = undefined,
  { sizeId: currentSizeId, productId, themeType },
  fallbackWhenNotFound = false
) {
  const normalizedSizeId = normalizeId(sizeId);

  if (normalizedSizeId) {
    // Check if size exists
    if (
      rootGetters[prefixGlassType(GlassesGetter.FIND_SIZE_BY_ID)](
        normalizedSizeId
      )
    ) {
      // Ensure the current product is available in the new size
      if (productId) {
        const matchingProducts = rootGetters[
          prefixGlassType(GlassesGetter.FILTER_MATCHING_PRODUCTS)
        ](themeType, normalizedSizeId, productId);

        if (!matchingProducts.length && !fallbackWhenNotFound) {
          throw new SizeIdNotAvailableError(
            `Current product (#${productId}) doesn't exist in size #${normalizedSizeId}`
          );
        }
      }

      return normalizedSizeId;
    } else {
      const message = `Size ID not found (#${normalizedSizeId}).`;

      if (fallbackWhenNotFound) {
        console.warn(`${message}. Using fallback`);
      } else {
        throw new SizeIdNotFoundError(message);
      }
    }
  }

  // Keep the current size ID if any
  if (currentSizeId) {
    console.log(`Keep current Size Id (#${currentSizeId})`);

    return currentSizeId;
  }

  console.log(`Fallback to the default size ID (#${DEFAULT_SIZE_ID})`);

  return DEFAULT_SIZE_ID;
}

/**
 *
 * @param {ConfiguratorState} state
 * @param rootGetters
 * @param {number|null} productId
 * @param {number|null} sizeId
 * @param {number|null} currentProductId
 * @param {number|null} categoryId
 * @param {ThemeType} themeType
 * @param fallbackWhenNotFound
 */
export function getProductId(
  { rootGetters },
  productId,
  { sizeId, productId: currentProductId, categoryId, themeType } = {},
  fallbackWhenNotFound = false
) {
  const normalizedProductId = normalizeId(productId);

  /**
   *
   * @param {ProductId} id
   * @return {ProductVariant[]}
   */
  const findProductVariants = id =>
    rootGetters[prefixGlassType(GlassesGetter.FILTER_MATCHING_PRODUCTS)](
      themeType,
      sizeId,
      id
    );

  const productsByCategory =
    rootGetters[prefixGlassType(GlassesGetter.GET_PRODUCTS_BY_CATEGORY)];

  const isProductAvailable = id => {
    if (findProductVariants(id).length === 0) {
      return false;
    }

    if (categoryId) {
      return productsByCategory[categoryId].includes(id);
    }

    return true;
  };

  if (normalizedProductId) {
    if (
      rootGetters[prefixGlassType(GlassesGetter.FIND_PRODUCT_BY_ID)](
        normalizedProductId
      )
    ) {
      if (isProductAvailable(normalizedProductId)) {
        return normalizedProductId;
      } else {
        const message = `Product ID not available`;

        if (fallbackWhenNotFound) {
          console.warn(`${message}. Using fallback`);
        } else {
          throw new ProductIdNotAvailableError(message);
        }
      }
    } else {
      const message = `Product ID not found`;

      if (fallbackWhenNotFound) {
        console.warn(`${message}. Using fallback`);
      } else {
        throw new ProductIdNotFoundError(message);
      }
    }
  }

  if (currentProductId && isProductAvailable(currentProductId)) {
    console.log(`Keep current product ID (#${currentProductId})`);

    return currentProductId;
  }

  // Fallback to a default theme
  const variants = findProductVariants();

  if (!variants.length) {
    throw new ProductIdError('No matching product variant found.');
  }

  const availableVariant = variants.find(variant =>
    isProductAvailable(variant.id)
  );

  if (!availableVariant) {
    throw new ProductIdError(
      'No matching product variant found in the current category.'
    );
  }

  const fallbackId = availableVariant.id;

  console.log(`Fallback to product ID #${fallbackId}`);

  return fallbackId;
}

/**
 *
 * @param rootGetters
 * @param colorId
 * @param currentColorId
 * @param currentGraduationColorId
 * @param themeType
 * @param sizeId
 * @param productId
 * @param quantityId
 * @param type
 * @param fallbackWhenNotFound
 * @return {ColorId|number}
 */
export function getColorId(
  { rootGetters },
  colorId,
  { colorId: currentColorId, themeType, sizeId, productId, quantityId },
  fallbackWhenNotFound = false
) {
  const normalizedColorId = normalizeId(colorId);
  const productVariant = rootGetters[
    prefixGlassType(GlassesGetter.FIND_PRODUCT_VARIANT)
  ](themeType, sizeId, productId, quantityId);
  const printTypeId =
    (productVariant && productVariant.printTypeId) || DEFAULT_PRINT_TYPE_ID;

  if (normalizedColorId) {
    if (rootGetters[prefixGlassType(GlassesGetter.FIND_COLOR_BY_ID)]) {
      if (isColorAvailable(printTypeId, normalizedColorId)) {
        return normalizedColorId;
      } else {
        const message = `Color ID not available (#${normalizedColorId})`;

        if (fallbackWhenNotFound) {
          console.warn(`${message}. Using fallback`);
        } else {
          throw new ColorIdNotAvailableError(message);
        }
      }
    } else {
      const message = `Color ID not found (#${normalizedColorId})`;

      if (fallbackWhenNotFound) {
        console.warn(`${message}. Using fallback`);
      } else {
        throw new ColorIdNotFoundError(message);
      }
    }
  }

  if (currentColorId && isColorAvailable(printTypeId, currentColorId)) {
    console.log(`Keep current color ID (#${currentColorId})`);

    return currentColorId;
  }

  console.log(`Fallback to color ID #${DEFAULT_COLOR_ID}`);

  return DEFAULT_COLOR_ID;
}

/**
 *
 * @param rootGetters
 * @param quantityId
 * @param sizeId
 * @param productId
 * @param currentQuantityId
 * @param fallbackWhenNotFound
 * @param {themeType} themeType
 * @return {QuantityId|null|number|*}
 */
export function getQuantityId(
  { rootGetters },
  quantityId,
  { sizeId, productId, quantityId: currentQuantityId, themeType },
  fallbackWhenNotFound = false
) {
  const normalizedQuantityId = normalizeId(quantityId);

  const availableQuantities = rootGetters[
    prefixGlassType(GlassesGetter.GET_PRODUCT_VARIANT_QUANTITIES)
  ](themeType, sizeId, productId);

  const matchingProductVariant = id =>
    availableQuantities.find(pv => pv.id === id);

  if (normalizedQuantityId) {
    if (
      rootGetters[prefixGlassType(GlassesGetter.FIND_QUANTITY_BY_ID)](
        normalizedQuantityId
      )
    ) {
      if (matchingProductVariant(normalizedQuantityId)) {
        return normalizedQuantityId;
      } else {
        const message = `Quantity ID not available (#${normalizedQuantityId})`;

        if (fallbackWhenNotFound) {
          console.warn(`${message}. Using fallback`);
        } else {
          throw new QuantityIdNotAvailableError(message);
        }
      }
    } else {
      const message = `Quantity ID not found (#${normalizedQuantityId})`;

      if (fallbackWhenNotFound) {
        console.warn(`${message}. Using fallback`);
      } else {
        throw new QuantityIdNotFoundError(message);
      }
    }
  }

  if (currentQuantityId && matchingProductVariant(currentQuantityId)) {
    console.log(`Keep current quantity ID (#${currentQuantityId})`);

    return currentQuantityId;
  }

  if (!availableQuantities.length) {
    throw new QuantityIdError('No matching product variant found.');
  }

  const fallbackId = availableQuantities[0].id;

  console.log(`Fallback to quantity ID #${fallbackId}`);

  return fallbackId;
}

export function getScreenPrintingColorId(
  { rootGetters },
  screenPrintingColorId,
  { screenPrintingColorId: currentScreenPrintingColorId },
  fallbackWhenNotFound = false
) {
  const normalizedScreenPrintingColorId = normalizeId(screenPrintingColorId);

  const findScreenPrintingColorById =
    rootGetters[
      prefixGlassType(GlassesGetter.FIND_SCREEN_PRINTING_COLOR_BY_ID)
    ];

  if (normalizedScreenPrintingColorId) {
    if (findScreenPrintingColorById(normalizedScreenPrintingColorId)) {
      return normalizedScreenPrintingColorId;
    } else {
      const message = `Screen PrintingColor ID not found (#${normalizedScreenPrintingColorId})`;

      if (fallbackWhenNotFound) {
        console.warn(`${message}. Using fallback`);
      } else {
        throw new ScreenPrintingColorIdNotFoundError(message);
      }
    }
  }

  // Keep the current screen printing color ID if any
  if (currentScreenPrintingColorId) {
    console.log(
      `Keep current screen printing color ID (#${currentScreenPrintingColorId})`
    );

    return currentScreenPrintingColorId;
  }

  console.log(
    `Fallback to the default screen printing color ID (#${DEFAULT_SCREEN_PRINTING_COLOR_ID})`
  );

  return DEFAULT_SCREEN_PRINTING_COLOR_ID;
}

const zoneCustomizationIdGenerator = AutoIncrementIdFactory(1);

/**
 *
 * @param {string} id
 * @param {string} type
 * @return {string}
 */
export function createZoneCustomizationIdFromZone(id, type) {
  // `c` prefix stands for 'customization'
  // `z` prefix stands for 'zone'
  // zone id and type are used to improve code understanding during development
  return `c${zoneCustomizationIdGenerator.next().value}__z${id}__${type}`;
}

/**
 *
 * @param {number} productId
 * @param {number} groupId
 * @return {string}
 */
export function getCustomizationGroupId(productId, groupId) {
  if (typeof productId !== 'number' || productId < 0) {
    throw new TypeError('Invalid product ID');
  }

  if (typeof groupId !== 'number' || groupId < 0) {
    throw new TypeError('Invalid group ID');
  }

  // `p` prefix stands for 'product'
  // `zg` prefix stands for 'zone group'
  return `p${productId}__zg${groupId}`;
}

/**
 *
 * @param {ConfiguratorState} state
 * @param {{groupId: ZoneGroupId, productId: ProductId, customization: ZoneCustomization|ZoneCustomizationText|ZoneCustomizationCustomConfigImage|ZoneCustomizationCustomConfigText}[]} list
 * @param overwrite
 */
export function setCustomizations(state, { list, overwrite = false }) {
  if (!Array.isArray(list)) {
    throw new TypeError('Invalid customization list.');
  }

  const customizationMapToAdd = {};
  const customizationListToAdd = {};

  list.forEach(
    ({ groupId = DEFAULT_ZONE_GROUP_ID, productId, customization }) => {
      // Set product ID (fFallback to the active product ID if undefined)
      const _productId =
        productId !== undefined ? productId : state.activeProductId;

      if (typeof _productId !== 'number' || _productId < 0) {
        throw new Error('Invalid product ID');
      }

      // Set group ID
      if (typeof groupId !== 'number' || groupId < 0) {
        throw new Error(`Invalid group ID (${typeof groupId})`);
      }

      const _groupId = getCustomizationGroupId(_productId, groupId);

      // Set customization ID
      if (!customization.templateZoneId) {
        throw new Error('Invalid template zone ID');
      }

      const id =
        customization.id ||
        createZoneCustomizationIdFromZone(
          customization.templateZoneId,
          customization.type
        );

      // Update map and list
      customizationMapToAdd[id] = { ...customization, id };

      if (customizationListToAdd[_groupId] === undefined) {
        if (overwrite) {
          customizationListToAdd[_groupId] = [];
        } else {
          const existingList = state.zoneCustomizationList[_groupId];

          customizationListToAdd[_groupId] =
            existingList !== undefined ? [...existingList] : [];
        }
      }

      if (!customizationListToAdd[_groupId].includes(id)) {
        customizationListToAdd[_groupId].push(id);
      }
    }
  );

  // Update store
  if (overwrite) {
    state.zoneCustomizationMap = customizationMapToAdd;
    state.zoneCustomizationList = customizationListToAdd;
  } else {
    const oldZoneCustomizationMap = { ...state.zoneCustomizationMap };
    const oldZoneCustomizationList = { ...state.zoneCustomizationList };

    state.zoneCustomizationMap = {
      ...state.zoneCustomizationMap,
      ...customizationMapToAdd,
    };

    state.zoneCustomizationList = {
      ...state.zoneCustomizationList,
      ...customizationListToAdd,
    };

    if (
      JSON.stringify(state.zoneCustomizationMap) !==
        JSON.stringify(oldZoneCustomizationMap) ||
      JSON.stringify(state.zoneCustomizationList) !==
        JSON.stringify(oldZoneCustomizationList)
    ) {
      state.isCurrentCustomerProjectDirty = true;
    }
  }
}

/**
 *
 * @param {array} customizationZones
 * @return {boolean}
 */
export function customizationsComplete(customizationZones) {
  // New version: returns true if at least one completedCustomization exists
  return customizationZones.some(customizationZone => {
    const completedCustomization = customizationZone.customizations.filter(
      customization => customization[ZoneTypeMap[customization.type]]
    );
    return completedCustomization.length > 0;
  });
}
